import createAxiosInstance from './createAxiosInstance'

const path = process.env.VUE_APP_HOUSEKEEPER_API
const instance = createAxiosInstance(`${path}/api/v1`, ['Authorization'])

/**
 * 人員功能權限 Enum
 * @enum {number}
 */
export const FeatureByUser = Object.freeze({
  Postal: 1, // 郵務寄放
  Announcement: 2, // 社區公告
  Message: 3, // 意見反映
  Push: 4, // 推播通知
  User: 5, // 住戶資訊
  Payment: 6, // 管理費
  Facilities: 7, // 公設預約
  Point: 8 // 點數管理
})

export const housekeeperAPI = {

  // * ---------- 使用者功能權限 ⬇️ ----------
  /**
   * 取得登入功能權限
   * @param {number} feature - 功能代碼（1~8）請參考 enum FeatureByUser
   * @returns {Promise} 登入功能權限資料
   * @see GET /api/v1/Login/Feature
   * Swagger: {@link https://housekeeper-api-dev.kingnetsmart.com.tw/swagger/index.html#/Login/get_api_v1_Login_Feature}
   */
  getFeatureByUser: (feature) => instance.get('/Login/Feature', {
    params: {
      Feature: feature
    }
  }),
  /**
   * 取得登入資訊
   * @returns {Promise} 登入資訊
   * @see GET /api/v1/Login/Info
   * Swagger: {@link https://housekeeper-api-dev.kingnetsmart.com.tw/swagger/index.html#/Login/get_api_v1_Login_Info}
   */
  getLoginInfo: () => instance.get('/Login/Info', {
    metadata: {
      // Error 時不由攔截器處理錯誤 Alert / Toast
      // issue: 拿過期的 Token 去打 API 導致出現了過期的提示
      // 短期：噴錯時不跳提示
      // 中長期：之後會請後端 set cookie 時給予正確的時間，沒有 cookie 就不會打 API
      handleErrorManually: true
    }
  }),
  // * ---------- 使用者功能權限 ⬆️ ----------

  // * ---------- 智慧櫃 ⬇️ ----------
  /**
   * 取得智慧櫃授權啟用狀態（目前沒用到這支 API）
   * @returns {Promise} 智慧櫃授權啟用狀態資料，Enable 目前只有 0, 1，但後端國益開 integer 便於後續擴增
   * @see GET /api/v1/SmartLocker/Owner
   * Swagger: {@link https://housekeeper-api-dev.kingnetsmart.com.tw/swagger/index.html#/SmartLocker/get_api_v1_SmartLocker_Owner}
   */
  getSmartLockerOwnerEnable: () => instance.get('/SmartLocker/Owner'),

  /**
   * 取得智慧櫃入櫃阻擋提示中的教學文件
   * @returns {Promise} TutorialUrl
   * @see GET /api/v1/SmartLocker/Supplier
   * Swagger: {@link https://housekeeper-api-dev.kingnetsmart.com.tw/swagger/index.html#/SmartLocker/get_api_v1_SmartLocker_Supplier}
   */
  getSmartLockerTutorialUrl: () => instance.get('/SmartLocker/Supplier'),
  // * ---------- 智慧櫃 ⬆️ ----------

  // * ---------- Badges API ⬇️ ----------
  /**
   * 取得未領取寄放物品數量
   * @returns {Promise} 未領取寄放物品數量
   * @see GET /api/v1/CollectionPayment/Uncollected/Count
   * Swagger: {@link https://housekeeper-api-dev.kingnetsmart.com.tw/swagger/index.html#/CollectionPayment/get_api_v1_CollectionPayment_Uncollected_Count}
   */
  getUncollectedCollectionPaymentCount: () => instance.get('/CollectionPayment/Uncollected/Count'),

  /**
   * 取得舊訪客抵達數量
   * @returns {Promise} 舊訪客抵達數量
   * @see GET /api/v1/Guest/Old/Arrived/Count
   * Swagger: {@link https://housekeeper-api-dev.kingnetsmart.com.tw/swagger/index.html#/Guest/get_api_v1_Guest_Old_Arrived_Count}
   */
  getOldGuestArrivedCount: () => instance.get('/Guest/Old/Arrived/Count'),

  /**
   * 取得未退貨郵務數量
   * @returns {Promise} 未退貨郵務數量
   * @see GET /api/v1/Postal/Return-Postal/Count
   * Swagger: {@link https://housekeeper-api-dev.kingnetsmart.com.tw/swagger/index.html#/Postal/get_api_v1_Postal_Return_Postal_Count}
   */
  getUnreturnedPostalCount: () => instance.get('/Postal/Return-Postal/Count'),

  /**
   * 取得未領取包裹數量
   * @returns {Promise} 未領取包裹數量
   * @see GET /api/v1/Postal/Unclaimed-Postal/Count
   * Swagger: {@link https://housekeeper-api-dev.kingnetsmart.com.tw/swagger/index.html#/Postal/get_api_v1_Postal_Unclaimed_Postal_Count}
   */
  getUnclaimedPostalCount: () => instance.get('/Postal/Unclaimed-Postal/Count'),
  // * ---------- Badges API ⬆️ ----------

  // * ---------- 管理費 API ⬇️ ----------
  /**
   * 取得社區管理費設定
   * @returns {Promise} 社區管理費設定
   * @see GET /api/v1/Bill/CommunityBillServiceSettings
   * Swagger: {@link https://housekeeper-api-dev.kingnetsmart.com.tw/swagger/index.html#/Bill/get_api_v1_Bill_CommunityBillServiceSettings}
   */
  getCommunityBillServiceSettings: () => instance.get('/Bill/CommunityBillServiceSettings', {
    metadata: {
      userHandlesErrors: true // 400 時不由攔截器處理錯誤 Alert
    }
  }),
  // * ---------- 管理費 API ⬆️ ----------

  // * ---------- 棟、樓、戶 API ⬇️ ----------
  /**
   * 取得社區戶別查詢棟別資料
   * @returns {Promise} 查詢棟別資料
   * @see GET /api/v1/CommunityTablet/Tag
   * Swagger: {@link https://housekeeper-api-dev.kingnetsmart.com.tw/swagger/index.html#/CommunityTablet/get_api_v1_CommunityTablet_Tag}
   */
  getCommunityTabletTag: () => instance.get('/CommunityTablet/Tag'),

  /**
   * 取得社區戶別查詢樓層資料
   * @param {string} tag - 棟別
   * @returns {Promise} 查詢樓層資料
   * @see GET /api/v1/CommunityTablet/{Tag}/Floor
   * Swagger: {@link https://housekeeper-api-dev.kingnetsmart.com.tw/swagger/index.html#/CommunityTablet/get_api_v1_CommunityTablet__Tag__Floor}
   */
  getCommunityTabletFloor: (tag) => instance.get(`/CommunityTablet/${tag}/Floor`),

  /**
   * 取得社區戶別查詢戶別資料
   * @param {string} tag - 棟別
   * @param {string} floor - 樓層
   * @returns {Promise} 查詢戶別資料
   * @see GET /api/v1/CommunityTablet/{Tag}/{Floor}/Tablet-Note
   * Swagger: {@link https://housekeeper-api-dev.kingnetsmart.com.tw/swagger/index.html#/CommunityTablet/get_api_v1_CommunityTablet__Tag___Floor__Tablet_Note}
   */
  getCommunityTabletNote: (tag, floor) => instance.get(`/CommunityTablet/${tag}/${floor}/Tablet-Note`),
  // * ---------- 棟、樓、戶 API ⬆️ ----------

  // * ---------- 住戶開通 ⬇️ ----------
  /**
   * 綁定住戶手機條碼裝置
   * @param {string} tabletId - 戶別 ID
   * @param {string} deviceSn - 裝置序號
   * @returns {Promise} 綁定住戶手機條碼裝置結果
   * @see POST /api/v1/CommunityTablet/{TabletId}/Device/Bind/Tablet/DeviceSn
   * Swagger: {@link https://housekeeper-api-dev.kingnetsmart.com.tw/swagger/index.html#/CommunityTablet/post_api_v1_CommunityTablet__TabletId__Device_Bind_Tablet_DeviceSn}
   */
  bindResidentDeviceSn: (tabletId, deviceSn) => instance.post(`/CommunityTablet/${tabletId}/Device/Bind/Tablet/DeviceSn`, { DeviceSn: deviceSn }),

  /**
   * 查詢住戶 NFC 門禁卡載具資料
   * @param {string} carrierCode - 載具號碼
   * @returns {Promise} 門禁卡載具資料
   * @see GET /api/v1/CommunityTablet/{CarrierCode}/Carrier
   * Swagger: {@link https://housekeeper-api-dev.kingnetsmart.com.tw/swagger/index.html#/CommunityTablet/get_api_v1_CommunityTablet__CarrierCode__Carrier}
   */
  getResidentCarrier: (carrierCode) => instance.get(`/CommunityTablet/${carrierCode}/Carrier`,
    {
      metadata: {
        handleErrorManually: true // 不由攔截器處理錯誤 Alert
      }
    }
  ),

  /**
   * 綁定住戶 NFC 門禁卡
   * @param {string} tabletId - 戶別 ID
   * @param {object} carrierCode - 載具資料
   * @returns {Promise} 綁定住戶 NFC 門禁卡結果
   * @see POST /api/v1/CommunityTablet/{TabletId}/Carrier/Bind/Tablet/Code
   * Swagger: {@link https://housekeeper-api-dev.kingnetsmart.com.tw/swagger/index.html#/CommunityTablet/post_api_v1_CommunityTablet__TabletId__Carrier_Bind_Tablet_Code}
   */
  bindResidentCarrierCode: (tabletId, carrierCode) => instance.post(`/CommunityTablet/${tabletId}/Carrier/Bind/Tablet/Code`, { CarrierCode: carrierCode }),

  /**
   * 啟用社區戶別開通序號
   * @param {string} tabletId - 戶別 ID
   * @returns {Promise} 啟用結果
   * @see POST /api/v1/CommunityTablet/{TabletId}/Device/Activate/Serial
   * Swagger: {@link https://housekeeper-api-dev.kingnetsmart.com.tw/swagger/index.html#/CommunityTablet/post_api_v1_CommunityTablet__TabletId__Device_Activate_Serial}
   */
  activateCommunityTabletDeviceSerial: (tabletId) => instance.post(`/CommunityTablet/${tabletId}/Device/Activate/Serial`),
  // * ---------- 住戶開通 ⬆️ ----------

  // * ---------- 住戶開通進階 ⬇️ ----------
  /**
   * 查詢社區戶別列表
   * @param {object} queryParams - 查詢參數（IsDeviceBind, Tag, IsVirtual）
   * @returns {Promise} 社區戶別列表資料
   * @see GET /api/v1/CommunityTablet/Query
   * Swagger: {@link https://housekeeper-api-dev.kingnetsmart.com.tw/swagger/index.html#/CommunityTablet/get_api_v1_CommunityTablet_Query}
   */
  getCommunityTabletList: async (queryParams) => instance.get('/CommunityTablet/Query', { params: queryParams }),

  /**
   * 更新是否出租
   * @param {string} tabletId - 戶別 ID
   * @param {object} rentData - 租賃資料
   * @returns {Promise} 更新是否出租結果
   * @see PATCH /api/v1/CommunityTablet/{TabletId}/IsRent
   * Swagger: {@link https://housekeeper-api-dev.kingnetsmart.com.tw/swagger/index.html#/CommunityTablet/patch_api_v1_CommunityTablet__TabletId__IsRent}
   */
  updateTabletIsRent: (tabletId, rentData) => instance.patch(`/CommunityTablet/${tabletId}/IsRent`, rentData,
    {
      metadata: {
        handleErrorManually: true // 不由攔截器處理錯誤 Alert
      }
    }
  ),
  /**
   * 新增虛擬戶
   * @param {string} tabletId - 戶別 ID
   * @param {object} virtualData - 虛擬戶資料，VirtualName 必填
   * @returns {Promise} 新增虛擬戶結果
   * @see POST /api/v1/CommunityTablet/{TabletId}/Virtual
   * Swagger: {@link https://housekeeper-api-dev.kingnetsmart.com.tw/swagger/index.html#/CommunityTablet/post_api_v1_CommunityTablet__TabletId__Virtual}
   */
  addVirtualTablet: (tabletId, virtualData) => instance.post(`/CommunityTablet/${tabletId}/Virtual`, virtualData,
    {
      metadata: {
        handleErrorManually: true // 不由攔截器處理錯誤 Alert
      }
    }
  ),
  /**
   * 刪除虛擬戶
   * @param {string} tabletId - 戶別 ID
   * @returns {Promise} 刪除虛擬戶結果
   * @see DELETE /api/v1/CommunityTablet/{TabletId}/Virtual
   * Swagger: {@link https://housekeeper-api-dev.kingnetsmart.com.tw/swagger/index.html#/CommunityTablet/delete_api_v1_CommunityTablet__TabletId__Virtual}
   */
  deleteVirtualTablet: (tabletId) => instance.delete(`/CommunityTablet/${tabletId}/Virtual`, {
    metadata: {
      handleErrorManually: true // 不由攔截器處理錯誤 Alert
    }
  }),
  /**
   * 更名虛擬戶
   * @param {string} tabletId - 戶別 ID
   * @param {object} renameData - 更名資料，VirtualName 必填
   * @returns {Promise} 更名虛擬戶結果
   * @see PATCH /api/v1/CommunityTablet/{TabletId}/Virtual
   * Swagger: {@link https://housekeeper-api-dev.kingnetsmart.com.tw/swagger/index.html#/CommunityTablet/patch_api_v1_CommunityTablet__TabletId__VirtualName}
   */
  renameVirtualTablet: (tabletId, renameData) => instance.patch(`/CommunityTablet/${tabletId}/VirtualName`, renameData,
    {
      metadata: {
        handleErrorManually: true // 不由攔截器處理錯誤 Alert
      }
    }
  ),
  /**
   * 查詢社區戶別裝置資料
   * @param {string} tabletId - 戶別 ID
   * @returns {Promise} 裝置查詢結果
   * @see GET /api/v1/CommunityTablet/{TabletId}/Device/Query
   * Swagger: {@link https://housekeeper-api-dev.kingnetsmart.com.tw/swagger/index.html#/CommunityTablet/get_api_v1_CommunityTablet__TabletId__Device_Query}
   */
  getCommunityTabletDeviceList: (tabletId) => instance.get(`/CommunityTablet/${tabletId}/Device/Query`),
  /**
   * 查詢社區戶別門禁卡磁扣載具資料
   * @param {string} tabletId - 戶別 ID
   * @returns {Promise} 裝置查詢結果
   * @see GET /api/v1/CommunityTablet/{TabletId}/Carrier/Query
   * Swagger: {@link https://housekeeper-api-dev.kingnetsmart.com.tw/swagger/index.html#/CommunityTablet/get_api_v1_CommunityTablet__TabletId__Carrier_Query}
   */
  getCommunityTabletCarrierList: (tabletId) => instance.get(`/CommunityTablet/${tabletId}/Carrier/Query`),
  /**
   * 解除社區戶別手機裝置綁定
   * @param {string} tabletId - 戶別 ID
   * @param {string} deviceSn - 裝置序號
   * @returns {Promise} 解除綁定結果
   * @see DELETE /api/v1/CommunityTablet/{TabletId}/Device/Unbind/DeviceSn/{DeviceSn}
   * Swagger: {@link https://housekeeper-api-dev.kingnetsmart.com.tw/swagger/index.html#/CommunityTablet/delete_api_v1_CommunityTablet__TabletId__Device_Unbind_DeviceSn__DeviceSn_}
   */
  unbindCommunityTabletDevice: (tabletId, deviceSn) => instance.delete(`/CommunityTablet/${tabletId}/Device/Unbind/DeviceSn/${deviceSn}`),
  /**
   * 解除社區戶別載具綁定
   * @param {string} tabletId - 戶別 ID
   * @param {string} carrierCardId - 載具卡片識別碼
   * @returns {Promise} 解除綁定結果
   * @see POST /api/v1/CommunityTablet/{TabletId}/Carrier/Unbind/CardId
   * Swagger: {@link https://housekeeper-api-dev.kingnetsmart.com.tw/swagger/index.html#/CommunityTablet/post_api_v1_CommunityTablet__TabletId__Carrier_Unbind_CardId}
   */
  unbindCommunityTabletCarrier: (tabletId, carrierCardId) => instance.post(`/CommunityTablet/${tabletId}/Carrier/Unbind/CardId`, { CarrierCardId: carrierCardId }),

  /**
   * 查詢社區戶別門禁卡載具資料（依載具號碼）
   * @param {string} carrierCode - 載具號碼
   * @returns {Promise} 門禁卡載具資料
   * @see GET /api/v1/CommunityTablet/{CarrierCode}/Carrier
   * Swagger: {@link https://housekeeper-api-dev.kingnetsmart.com.tw/swagger/index.html#/CommunityTablet/get_api_v1_CommunityTablet__CarrierCode__Carrier}
   */
  getCommunityTabletCarrier: (carrierCode) => instance.get(`/CommunityTablet/${carrierCode}/Carrier`),
  /**
   * 查詢社區戶別門禁卡載具資料（依戶別及載具號碼查詢）
   * @param {string} tabletId - 戶別 ID
   * @param {string} carrierCode - 載具號碼
   * @returns {Promise} 門禁卡載具資料
   * @see GET /api/v1/CommunityTablet/{TabletId}/Carrier/Tablet/Query/Code
   */
  getCommunityTabletCarrierByTabletAndCode: (tabletId, carrierCode) => instance.get(`/CommunityTablet/${tabletId}/Carrier/Tablet/Query/Code`, {
    params: {
      CarrierCode: carrierCode
    }
  }),
  /**
   * 該戶別的區權戶與承租戶清單
   * @param {string} tabletId - 戶別 ID
   * @returns {Promise} 更新結果
   * @see GET /api/v1/CommunityTablet/{TabletId}/Regional-And-Virtual
   * Swagger: {@link https://housekeeper-api-dev.kingnetsmart.com.tw/swagger/index.html#/CommunityTablet/patch_api_v1_CommunityTablet__TabletId__Regional_And_Virtual}
   */
  getTabletRegionalAndVirtual: (tabletId) => instance.get(`/CommunityTablet/${tabletId}/Regional-And-Virtual`),
  /**
   * 調整社區戶別裝置
   * @param {string} tabletId - 戶別 ID
   * @param {object} adjustData - 調整裝置資料
   * @returns {Promise} 調整結果
   * @see PATCH /api/v1/CommunityTablet/{TabletId}/Device/Adjust/Tablet
   * Swagger: {@link https://housekeeper-api-dev.kingnetsmart.com.tw/swagger/index.html#/CommunityTablet/patch_api_v1_CommunityTablet__TabletId__Device_Adjust_Tablet}
   */
  adjustCommunityTabletDevice: (tabletId, adjustData) => instance.patch(`/CommunityTablet/${tabletId}/Device/Adjust/Tablet`, adjustData),
  /**
   * 調整社區戶別載具
   * @param {string} tabletId - 戶別 ID
   * @param {object} adjustTablets - 調整載具資料
   * @returns {Promise} 調整結果
   * @see PATCH /api/v1/CommunityTablet/{TabletId}/Carrier/Adjust/Tablet
   * Swagger: {@link https://housekeeper-api-dev.kingnetsmart.com.tw/swagger/index.html#/CommunityTablet/patch_api_v1_CommunityTablet__TabletId__Carrier_Adjust_Tablet}
   */
  adjustCommunityTabletCarrier: (tabletId, adjustTablets) => instance.patch(`/CommunityTablet/${tabletId}/Carrier/Adjust/Tablet`, adjustTablets),
  // * ---------- 住戶開通進階 ⬆️ ----------

  // * ---------- 快速領取 ⬇️ ----------
  /**
   * 取得社區戶別裝置資料
   * @param {string} deviceSn - 裝置序號
   * @returns {Promise} 裝置資料
   * @see GET /api/v1/CommunityTablet/{DeviceSn}/Device
   * Swagger: {@link https://housekeeper-api-dev.kingnetsmart.com.tw/swagger/index.html#/CommunityTablet/get_api_v1_CommunityTablet__DeviceSn__Device}
   */
  getHouseholdListByDeviceCode: (deviceSn) => instance.get(`/CommunityTablet/${deviceSn}/Device`,
    {
      metadata: {
        handleErrorManually: true // 不由攔截器處理錯誤 Alert
      }
    }
  ),

  /**
   * 查詢社區戶別 NFC 門禁卡載具資料（直接回傳 axios promise）
   * @param {string} carrierCode - 載具號碼
   * @returns {Promise} axios promise
   * @see GET /api/v1/CommunityTablet/{CarrierCode}/Carrier
   * Swagger: {@link https://housekeeper-api-dev.kingnetsmart.com.tw/swagger/index.html#/CommunityTablet/get_api_v1_CommunityTablet__CarrierCode__Carrier}
   */
  getHouseholdListByCarrier: (carrierCode) => {
    // 直接回傳 axios promise，無額外處理
    return instance.get(`/CommunityTablet/${carrierCode}/Carrier`)
  },

  /**
   * 依戶別取得未領取包裹的資料
   * @param {Array<string>} tabletIds - 戶別 ID 陣列
   * @returns {Promise} 其未領取包裹資料
   * @see GET /api/v1/Postal/Uncollected/Tablets
   * Swagger: {@link https://housekeeper-api-dev.kingnetsmart.com.tw/swagger/index.html#/Postal/get_api_v1_Postal_Uncollected_Tablets}
   */
  getPostalUncollectedByTablets: (tabletIds) => instance.get('/Postal/Uncollected/Tablets', {
    params: {
      TabletIds: tabletIds
    }
  }),

  /**
   * 依戶別取得未領取寄放的資料
   * @param {Array<string>} tabletIds - 戶別 ID 陣列
   * @returns {Promise} 其未領取寄放資料
   * @see GET /api/v1/CollectionPayment/Uncollected/Tablets
   * Swagger: {@link https://housekeeper-api-dev.kingnetsmart.com.tw/swagger/index.html#/CollectionPayment/get_api_v1_CollectionPayment__Uncollected__Tablets}
   */
  getCollectionUncollectedByTablets: (tabletIds) => instance.get('/CollectionPayment/Uncollected/Tablets', {
    params: {
      TabletIds: tabletIds
    }
  }),

  /**
   * 快速領取郵務結單
   * @param {object} data - 郵務結單資料
   * @returns {Promise} 結單結果
   * @see PATCH /api/v1/Postal/Uncollected/Status
   * Swagger: {@link https://housekeeper-api-dev.kingnetsmart.com.tw/swagger/index.html#/Postal/patch_api_v1_Postal_Uncollected_Status}
   */
  receivePostal: (data) => instance.patch('/Postal/Uncollected/Status', data),

  /**
   * 快速領取寄放結單
   * @param {object} data - 寄放結單資料
   * @returns {Promise} 結單結果
   * @see PATCH /api/v1/CollectionPayment/Uncollected/Status
   * Swagger: {@link https://housekeeper-api-dev.kingnetsmart.com.tw/swagger/index.html#/CollectionPayment/patch_api_v1_CollectionPayment__Uncollected__Status}
   */
  receiveCollection: (data) => instance.patch('/CollectionPayment/Uncollected/Status', data),
  // * ---------- 快速領取 ⬆️ ----------

  // * ---------- 行事曆 ⬇️ ----------
  /**
   * 取得行事曆檔案上傳 GCS 的簽章資訊（SignedUrl 與 Fields）
   * 整批檔案共用同一組簽章，呼叫一次即可，不需在迴圈中重複取得
   * @returns {Promise} 簽章資訊（Data.SignedUrl、Data.Fields）
   * @see POST /api/v1/CommunityCalendar/SignedUrl
   * Swagger: {@link https://housekeeper-api-dev.kingnetsmart.com.tw/swagger/index.html#/CommunityCalendar/post_api_v1_CommunityCalendar_SignedUrl}
   */
  getCommunityCalendarSignedUrl: () => instance.post('/CommunityCalendar/SignedUrl'),
  // * ---------- 行事曆 ⬆️ ----------

  /**
  * 取得蓋版廣告
  * Swagger: https://housekeeper-api-dev.kingnetsmart.com.tw/swagger/index.html#/Modal/get_api_v1_Modal
  */
  getAdModal: () => instance.get('/Modal',
    {
      metadata: {
        handleErrorManually: true // 不由攔截器處理錯誤 Alert
      }
    })
}
